/**
 * Password Validation Utilities
 * Secure password validation and generation functions
 */

// Common sequences to check against
const numSequences = ['012', '123', '234', '345', '456', '567', '678', '789'];
const alphaSequences = ['abc', 'bcd', 'cde', 'def', 'efg', 'fgh', 'ghi', 'hij', 'ijk', 'jkl', 'klm', 'lmn', 'mno', 'nop', 'opq', 'pqr', 'qrs', 'rst', 'stu', 'tuv', 'uvw', 'vwx', 'wxy', 'xyz'];
const qwertySequences = ['qwe', 'wer', 'ert', 'rty', 'tyu', 'yui', 'uio', 'iop', 'asd', 'sdf', 'dfg', 'fgh', 'ghj', 'hjk', 'jkl', 'zxc', 'xcv', 'cvb', 'vbn', 'bnm'];

// Error messages
const errorMessages = {
  wordLength: 'Password must be at least 8 characters long',
  wordNotEmail: 'Password should not be an email address',
  wordSequences: 'Password should not contain sequences',
  wordLowercase: 'Password must contain at least one lowercase letter',
  wordUppercase: 'Password must contain at least one uppercase letter',
  wordOneNumber: 'Password must contain at least one number',
  wordOneSpecialChar: 'Password must contain at least one special character'
};

/**
 * Validate password strength and return score with errors
 * @param {string} pwd - Password to validate
 * @returns {{ score: number, errors: string[] }}
 */
export const validatePassword = (pwd) => {
  const validationErrors = [];
  let score = 0;

  if (pwd.length < 8) {
    validationErrors.push(errorMessages.wordLength);
  } else {
    score += 10;
  }

  if (pwd.includes('@') && pwd.includes('.')) {
    validationErrors.push(errorMessages.wordNotEmail);
  } else {
    score += 10;
  }

  if (!/[a-z]/.test(pwd)) {
    validationErrors.push(errorMessages.wordLowercase);
  } else {
    score += 15;
  }

  if (!/[A-Z]/.test(pwd)) {
    validationErrors.push(errorMessages.wordUppercase);
  } else {
    score += 15;
  }

  if (!/\d/.test(pwd)) {
    validationErrors.push(errorMessages.wordOneNumber);
  } else {
    score += 15;
  }

  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\?]/.test(pwd)) {
    validationErrors.push(errorMessages.wordOneSpecialChar);
  } else {
    score += 15;
  }

  if (numSequences.some(seq => pwd.toLowerCase().includes(seq)) ||
      alphaSequences.some(seq => pwd.toLowerCase().includes(seq)) ||
      qwertySequences.some(seq => pwd.toLowerCase().includes(seq))) {
    validationErrors.push(errorMessages.wordSequences);
    score -= 10;
  } else {
    score += 10;
  }

  if (pwd.length > 12) score += 10;
  if (pwd.length > 16) score += 10;

  return { score: Math.max(0, Math.min(100, score)), errors: validationErrors };
};

/**
 * Per-rule pass/fail for the displayed checklist, mirroring the checks in
 * validatePassword. Presentation only — the score is unchanged. Returns the
 * five rules the checklist shows (the noEmail rule is validated but not shown:
 * this product has no email, so displaying it is a puzzle). The `key` matches
 * the `auth.passwordRules.*` i18n keys.
 * @param {string} pwd
 * @returns {{ key: string, met: boolean }[]}
 */
export const evaluatePasswordRules = (pwd = '') => {
  const lower = pwd.toLowerCase();
  const hasSequence = [...numSequences, ...alphaSequences, ...qwertySequences]
    .some((seq) => lower.includes(seq));
  return [
    { key: 'minLength', met: pwd.length >= 8 },
    { key: 'uppercase', met: /[A-Z]/.test(pwd) && /[a-z]/.test(pwd) },
    { key: 'number', met: /\d/.test(pwd) },
    { key: 'special', met: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>?]/.test(pwd) },
    { key: 'noSequences', met: pwd.length > 0 && !hasSequence },
  ];
};

/**
 * Get password strength verdict
 * @param {number} score - Password score
 * @returns {string}
 */
export const getVerdict = (score) => {
  if (score < 30) return 'Very Weak';
  if (score < 50) return 'Weak';
  if (score < 70) return 'Fair';
  if (score < 90) return 'Good';
  if (score < 100) return 'Strong';
  return 'Perfect';
};

/**
 * Get Ant Design progress status based on score
 * @param {number} score - Password score
 * @returns {'exception' | 'normal' | 'success'}
 */
export const getProgressStatus = (score) => {
  if (score < 30) return 'exception';
  if (score < 70) return 'normal';
  return 'success';
};

/**
 * Get progress color based on score
 * @param {number} score - Password score
 * @returns {string}
 */
export const getProgressColor = (score) => {
  if (score < 30) return 'var(--color-fn-garnet)';
  if (score < 50) return 'var(--color-fn-terracotta)';
  if (score < 70) return 'var(--color-fn-saffron)';
  if (score < 90) return 'var(--color-fn-majolica)';
  if (score < 100) return 'var(--color-fn-pistachio)';
  return 'var(--color-fn-plum)';
};

/**
 * Generate a cryptographically secure random integer
 * @param {number} max - Maximum value (exclusive)
 * @returns {number}
 */
const secureRandomInt = (max) => {
  const randomValues = new Uint32Array(1);
  window.crypto.getRandomValues(randomValues);
  return randomValues[0] % max;
};

/**
 * Securely shuffle an array
 * @param {Array} array - Array to shuffle
 * @returns {Array}
 */
const secureShuffle = (array) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = secureRandomInt(i + 1);
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

/**
 * Check if password contains common sequences
 * @param {string} password - Password to check
 * @returns {boolean}
 */
const hasSequence = (password) => {
  const lower = password.toLowerCase();
  return [...numSequences, ...alphaSequences, ...qwertySequences].some(seq =>
    lower.includes(seq) || lower.includes(seq.split('').reverse().join(''))
  );
};

/**
 * Generate a secure random password
 * @param {number} length - Password length (default: 12)
 * @returns {string}
 */
export const generateSecurePassword = (length = 12) => {
  const upperCase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lowerCase = 'abcdefghijklmnopqrstuvwxyz';
  const digits = '0123456789';
  const special = '!@#$%^&*()_+-=[]{}|;:,.<>?';

  const generatePassword = () => {
    let password = '';
    password += upperCase[secureRandomInt(upperCase.length)];
    password += digits[secureRandomInt(digits.length)];
    password += special[secureRandomInt(special.length)];

    const allChars = upperCase + lowerCase + digits + special;
    for (let i = password.length; i < length; i++) {
      password += allChars[secureRandomInt(allChars.length)];
    }

    return secureShuffle(password.split('')).join('');
  };

  let password;
  let attempts = 0;
  do {
    password = generatePassword();
    attempts++;
  } while (hasSequence(password) && attempts < 50);

  return password;
};

